import React, { useState, useEffect } from 'react';

interface InstallPromptProps {
  className?: string;
  buttonText?: string;
  description?: string;
}

// Détection de l'environnement de développement
const isDevelopment = () => {
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.port === '3000' || 
         window.location.port === '3001';
};

// Détection des appareils iOS/Safari
const isIOS = () => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod|Macintosh/.test(ua) && 
         (/Safari/.test(ua) || /AppleWebKit/.test(ua));
};

const InstallPrompt: React.FC<InstallPromptProps> = ({
  className = '',
  buttonText = 'Installer l\'application',
  description = 'Installez cette application sur votre appareil pour un accès rapide et une meilleure expérience'
}) => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [isAppleDevice, setIsAppleDevice] = useState(false);

  useEffect(() => {
    // Vérifier si c'est un appareil iOS/Safari
    setIsAppleDevice(isIOS());

    // Vérifier si la prompt a été fermée précédemment
    const promptWasClosed = localStorage.getItem('installPromptDismissed');
    if (promptWasClosed) {
      const dismissedTime = parseInt(promptWasClosed, 10);
      const currentTime = Date.now();
      // Réafficher après 7 jours
      if (currentTime - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setShowPrompt(false);
      }
    }

    // Vérifier si c'est un appareil Android
    const ua = navigator.userAgent.toLowerCase();
    const isAndroidDevice = ua.indexOf('android') > -1;
    setIsAndroid(isAndroidDevice);

    // En mode développement, afficher une UI de test si demandé
    if (isDevelopment() && window.location.search.includes('show-install-ui')) {
      console.log('Mode développement: Affichage forcé de l\'UI d\'installation');
      setIsInstallable(true);
      setIsAndroid(true);
      setIsAppleDevice(false); // Forcer à false pour les tests
      return;
    }

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      // Empêcher Chrome d'afficher automatiquement le prompt
      e.preventDefault();
      // @ts-expect-error - Propriété deferredPrompt ajoutée à window
      window.deferredPrompt = e;
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Écouter l'événement appinstalled
    const handleAppInstalled = () => {
      setIsInstallable(false);
      // @ts-expect-error - Propriété deferredPrompt ajoutée à window
      window.deferredPrompt = null;
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Nettoyage
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    // En mode développement, simuler une installation réussie
    if (isDevelopment() && window.location.search.includes('show-install-ui')) {
      console.log('Mode développement: Simulation d\'installation réussie');
      setIsInstallable(false);
      return;
    }

    // @ts-expect-error - Propriété deferredPrompt ajoutée à window
    const deferredPrompt = window.deferredPrompt;
    if (!deferredPrompt) {
      console.log('Le prompt d\'installation n\'est pas disponible');
      return;
    }

    try {
      // Afficher le prompt d'installation
      await deferredPrompt.prompt();
      console.log('Prompt d\'installation affiché');
      
      // Réinitialiser le deferredPrompt - il ne peut être utilisé qu'une fois
      // @ts-expect-error - Propriété deferredPrompt ajoutée à window
      window.deferredPrompt = null;
      setIsInstallable(false);
    } catch (error) {
      console.error('Erreur lors de l\'affichage du prompt d\'installation:', error);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Enregistrer dans localStorage que l'utilisateur a fermé le prompt
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  // Ne pas afficher le composant dans les cas suivants:
  // 1. Si l'application n'est pas installable
  // 2. Si ce n'est pas Android ET ce n'est pas le mode développement
  // 3. Si l'utilisateur a fermé le prompt
  // 4. Si c'est un appareil iOS/Safari (géré par react-ios-pwa-prompt)
  if (!isInstallable || (!isAndroid && !isDevelopment()) || !showPrompt || isAppleDevice) {
    return null;
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 bg-blue-800 text-white p-4 shadow-lg z-50 ${className}`}>
      <div className="flex items-start justify-between max-w-screen-md mx-auto">
        <div className="flex-1 pr-4">
          <p className="mb-3 text-sm">{description}</p>
          <button
            id="install-button"
            onClick={handleInstallClick}
            className="bg-white text-blue-800 px-4 py-2 rounded-md font-medium text-sm"
          >
            {buttonText}
          </button>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white hover:text-gray-200 flex-shrink-0"
          aria-label="Fermer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {isDevelopment() && (
        <div className="mt-2 text-xs opacity-70 text-center">
          Mode développement - Simulation d'installation
        </div>
      )}
    </div>
  );
};

export default InstallPrompt; 